import { useState } from 'react'
import { getFoodGuidance } from '../utils/foodGuidance'

// Foods You Should Eat / Foods to Avoid (+ a starter recipe), tailored to the
// conditions detected from the user's quiz answers. General wellness info —
// renders with a disclaimer; not medical nutrition therapy. (Added 2026-06-03.)
export default function FoodGuidance({ answers }) {
  const conditions = getFoodGuidance(answers)
  // Start with the first (highest-priority) condition expanded.
  const [openKey, setOpenKey] = useState(conditions[0]?.key || null)

  if (!conditions.length) return null

  return (
    <div style={{ marginTop: '40px', marginBottom: '40px' }}>
      <h3 style={{ fontSize: '1.3rem', marginBottom: '6px', color: '#333' }}>
        🥗 Foods For Your Health — Where to Start
      </h3>
      <p style={{ color: '#555', marginBottom: '20px', fontSize: '0.95rem', lineHeight: 1.5 }}>
        Based on your answers, food is one of the fastest levers you can pull. Here's a
        starting point for each area — what to eat more of, what to pull back, and one easy recipe.
      </p>

      {conditions.map((c) => {
        const isOpen = openKey === c.key
        return (
          <div
            key={c.key}
            style={{
              marginBottom: '14px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : c.key)}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '16px 18px',
                background: isOpen ? '#f0fdfa' : '#f9fafb',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#0f766e',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{c.label}</span>
              <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{isOpen ? '–' : '+'}</span>
            </button>

            {isOpen && (
              <div style={{ padding: '18px' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.55, marginBottom: '18px' }}>
                  {c.intro}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                  {/* Eat */}
                  <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '14px', borderLeft: '4px solid #16a34a' }}>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#15803d', marginBottom: '10px' }}>✅ Foods You Should Eat</h4>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {c.eat.map((item, i) => (
                        <li key={i} style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.5, marginBottom: '6px' }}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Avoid */}
                  <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '14px', borderLeft: '4px solid #dc2626' }}>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#b91c1c', marginBottom: '10px' }}>🚫 Foods to Avoid</h4>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {c.avoid.map((item, i) => (
                        <li key={i} style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.5, marginBottom: '6px' }}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* First step */}
                <div style={{ marginTop: '16px', background: '#fffbeb', borderRadius: '8px', padding: '12px 14px', borderLeft: '4px solid #f59e0b' }}>
                  <p style={{ fontSize: '0.9rem', color: '#92400e', lineHeight: 1.5, margin: 0 }}>
                    <strong>Start here:</strong> {c.firstStep}
                  </p>
                </div>

                {/* Recipe */}
                {c.recipe && (
                  <div style={{ marginTop: '16px', background: '#f8fafc', borderRadius: '8px', padding: '14px', border: '1px solid #e2e8f0' }}>
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>👩‍🍳 Easy Recipe: {c.recipe.name}</h4>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic', marginBottom: '10px', lineHeight: 1.5 }}>{c.recipe.why}</p>
                    <p style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '8px' }}>
                      <strong>You'll need:</strong> {c.recipe.ingredients.join(' · ')}
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#374151', lineHeight: 1.55, margin: 0 }}>
                      <strong>Make it:</strong> {c.recipe.steps}
                    </p>
                  </div>
                )}

                {/* Per-condition caution */}
                {c.caution && (
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.5, marginTop: '14px', fontStyle: 'italic' }}>
                    ⚠️ {c.caution}
                  </p>
                )}
              </div>
            )}
          </div>
        )
      })}

      <p style={{ fontSize: '0.78rem', color: '#9ca3af', lineHeight: 1.5, marginTop: '14px' }}>
        This is general wellness information to get you started — not personalized medical or dietary advice.
        Talk to your doctor or a registered dietitian before making major changes, especially if you have kidney
        disease, take prescription medication, or are in treatment for a serious condition.
      </p>
    </div>
  )
}
